// apps/widget/src/components/score-bar.tsx
//
// A 0–100 scale with the three bands named and the score pinned on it.
//
// A bare number answers "what did I get"; it does not answer "is that good?".
// Naming the bands and showing where the score falls answers both at a glance,
// which is the difference between a result a patient trusts and one they have
// to be talked through.

import { BAND_COLORS } from '../lib/theme';
import { t } from '../lib/i18n';

type Band = 'poor' | 'fair' | 'average' | 'good';

/** Upper bound of each band, matching the scoring thresholds. */
const BANDS: { band: Band; upTo: number }[] = [
  { band: 'fair', upTo: 60 },
  { band: 'average', upTo: 80 },
  { band: 'good', upTo: 100 },
];

export function ScoreBar({ score, band }: { score: number; band: string }) {
  const clamped = Math.max(0, Math.min(100, score));
  const colour = BAND_COLORS[band as keyof typeof BAND_COLORS] ?? BAND_COLORS.average;

  return (
    <div className="gs-scorebar">
      <div className="gs-scorebar__ends">
        <span>0</span>
        <span>100</span>
      </div>

      <div className="gs-scorebar__track">
        <div
          className="gs-scorebar__fill"
          style={{ width: `${clamped}%`, background: colour }}
        />
        {/* A drop standing on the bar, its point on the score. The number is
            counter-rotated so only the shape turns. */}
        <span className="gs-scorebar__pin" style={{ left: `${clamped}%` }}>
          <span className="gs-scorebar__drop" style={{ background: colour }} />
          <span className="gs-scorebar__value">{clamped}</span>
        </span>
      </div>

      {/* Band names sit under the boundaries they describe, so the reader can
          see which one the pin has landed in without a legend. */}
      <div className="gs-scorebar__bands" aria-hidden="true">
        {BANDS.map(({ band: b, upTo }) => (
          <span key={b} style={{ left: `${upTo - 10}%` }} data-active={b === band || undefined}>
            {t(`report.band.${b}`)}
          </span>
        ))}
      </div>
    </div>
  );
}
